import { useState } from "react"
import { usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

function formatUkPhone(value: string): string {
  const digits = value.replace(/[^\d+]/g, "")
  // Strip +44 prefix and normalise to 0
  const normalised = digits.startsWith("+44") ? "0" + digits.slice(3) : digits
  const d = normalised.replace(/\D/g, "")
  // 02x numbers: 020 xxxx xxxx
  if (d.startsWith("02")) {
    return d.length <= 3 ? d : d.length <= 7 ? `${d.slice(0, 3)} ${d.slice(3)}` : `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7, 11)}`
  }
  // 01x1 / 011x numbers: 0xxx xxx xxxx
  if (/^01[1-9]1|^011[3-9]/.test(d)) {
    return d.length <= 4 ? d : d.length <= 7 ? `${d.slice(0, 4)} ${d.slice(4)}` : `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7, 11)}`
  }
  // 07x mobile: 07xxx xxxxxx
  if (d.startsWith("07")) {
    return d.length <= 5 ? d : `${d.slice(0, 5)} ${d.slice(5, 11)}`
  }
  // Other 01xxx: 01xxx xxxxxx
  if (d.startsWith("01")) {
    return d.length <= 5 ? d : `${d.slice(0, 5)} ${d.slice(5, 11)}`
  }
  return d.slice(0, 11)
}

function isValidUkPhone(value: string): boolean {
  if (!value.trim()) return true // optional field
  const digits = value.replace(/\D/g, "")
  return /^0[1-9]\d{8,9}$/.test(digits)
}

function formatUkPostcode(value: string): string {
  const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "")
  if (clean.length <= 4) return clean
  // Insert space before last 3 characters (inward code)
  return `${clean.slice(0, -3)} ${clean.slice(-3)}`
}

function isValidUkPostcode(value: string): boolean {
  if (!value.trim()) return true // optional field
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value.trim())
}

export default function BrandSetupStep({
  accountType,
  initialData,
  onNext,
}: {
  accountType: string
  initialData: {
    agency_name?: string
    agency_address?: string
    agency_phone?: string
    brand_colour?: string
    website_url?: string
  }
  onNext: (data: Record<string, string>) => void
}) {
  const serverErrors = (usePage().props as { errors?: Record<string, string[]> }).errors || {}
  const [agencyName, setAgencyName] = useState(initialData.agency_name || "")
  const [agencyAddress, setAgencyAddress] = useState(initialData.agency_address || "")
  const [agencyPhone, setAgencyPhone] = useState(initialData.agency_phone || "")
  const [brandColour, setBrandColour] = useState(initialData.brand_colour || "#C41E2A")
  const [websiteUrl, setWebsiteUrl] = useState(initialData.website_url || "")
  const [errors, setErrors] = useState<Record<string, string>>({})
  const isAgency = accountType === "agency"

  function validate(): boolean {
    const newErrors: Record<string, string> = {}

    if (isAgency && !agencyName.trim()) {
      newErrors.agency_name = "Agency name is required."
    }

    if (isAgency && agencyPhone.trim() && !isValidUkPhone(agencyPhone)) {
      newErrors.agency_phone = "Enter a valid UK phone number."
    }

    if (isAgency && agencyAddress.trim()) {
      // Check the last line looks like a postcode
      const lines = agencyAddress.trim().split("\n")
      const lastLine = lines[lines.length - 1].trim()
      // If they've written something but no valid postcode on last line
      if (lines.length > 1 && !isValidUkPostcode(lastLine) && !/\d/.test(lastLine)) {
        // Only warn if they have multiple lines — they probably forgot the postcode
      }
    }

    if (brandColour && !/^#[0-9A-Fa-f]{6}$/.test(brandColour)) {
      newErrors.brand_colour = "Must be a valid hex colour (e.g. #C41E2A)."
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleSubmit() {
    if (!validate()) return

    const data: Record<string, string> = {
      brand_colour: brandColour,
      website_url: websiteUrl,
    }
    if (isAgency) {
      data.agency_name = agencyName
      data.agency_address = agencyAddress
      data.agency_phone = agencyPhone
    }
    onNext(data)
  }

  function clearError(field: string) {
    setErrors((prev) => { const { [field]: _, ...rest } = prev; return rest })
  }

  function fieldError(field: string): string | undefined {
    return errors[field] || serverErrors[field]?.[0]
  }

  return (
    <div className="text-center">
      <h2 className="font-display text-3xl font-semibold tracking-tight text-charcoal sm:text-4xl">
        {isAgency ? "Tell us about your agency" : "Brand your videos"}
      </h2>
      <p className="mt-3 text-warm-grey">
        {isAgency
          ? "We'll use these details in your video templates."
          : "Choose a colour that represents your brand."}
      </p>

      <div className="mx-auto mt-10 max-w-md space-y-5 text-left">
        {isAgency && (
          <>
            <div>
              <Label htmlFor="agency_name">Agency name</Label>
              <Input
                id="agency_name"
                value={agencyName}
                onChange={(e) => { setAgencyName(e.target.value); clearError("agency_name") }}
                placeholder="Smiths & Co Estate Agents"
                className="mt-1.5"
                aria-invalid={!!fieldError("agency_name")}
              />
              {fieldError("agency_name") && (
                <p className="mt-1 text-sm text-red">{fieldError("agency_name")}</p>
              )}
            </div>

            <div>
              <Label htmlFor="agency_address">Office address</Label>
              <textarea
                id="agency_address"
                value={agencyAddress}
                onChange={(e) => { setAgencyAddress(e.target.value); clearError("agency_address") }}
                onBlur={() => {
                  // Auto-format postcode on the last non-empty line
                  const lines = agencyAddress.split("\n")
                  let lastIdx = -1
                  for (let i = lines.length - 1; i >= 0; i--) { if (lines[i].trim()) { lastIdx = i; break } }
                  if (lastIdx >= 0 && /^[A-Z]{1,2}\d/i.test(lines[lastIdx].trim())) {
                    lines[lastIdx] = formatUkPostcode(lines[lastIdx].trim())
                    setAgencyAddress(lines.join("\n"))
                  }
                }}
                placeholder={"123 High Street\nEdinburgh\nEH1 1AA"}
                rows={3}
                className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm"
              />
              <p className="mt-1 text-xs text-warm-grey">
                Include postcode on the last line.
              </p>
            </div>

            <div>
              <Label htmlFor="agency_phone">Phone number</Label>
              <Input
                id="agency_phone"
                type="tel"
                value={agencyPhone}
                onChange={(e) => { setAgencyPhone(formatUkPhone(e.target.value)); clearError("agency_phone") }}
                placeholder="0131 555 0000"
                className="mt-1.5"
                aria-invalid={!!fieldError("agency_phone")}
              />
              {fieldError("agency_phone") && (
                <p className="mt-1 text-sm text-red">{fieldError("agency_phone")}</p>
              )}
            </div>
          </>
        )}

        <div>
          <Label htmlFor="brand_colour">Brand colour</Label>
          <div className="mt-1.5 flex items-center gap-3">
            <input
              type="color"
              id="brand_colour"
              value={brandColour}
              onChange={(e) => { setBrandColour(e.target.value); clearError("brand_colour") }}
              className="size-10 cursor-pointer rounded-lg border border-stone/40"
            />
            <Input
              value={brandColour}
              onChange={(e) => { setBrandColour(e.target.value); clearError("brand_colour") }}
              placeholder="#C41E2A"
              className="font-mono text-sm"
              aria-invalid={!!fieldError("brand_colour")}
            />
          </div>
          {fieldError("brand_colour") ? (
            <p className="mt-1 text-sm text-red">{fieldError("brand_colour")}</p>
          ) : (
            <p className="mt-1 text-xs text-warm-grey">
              This colour appears in your video templates.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="website_url">Website (optional)</Label>
          <Input
            id="website_url"
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://www.smithsandco.co.uk"
            className="mt-1.5"
          />
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        className="mt-10 bg-red px-10 py-5 text-sm font-semibold text-white hover:bg-red-deep"
      >
        Continue
      </Button>
    </div>
  )
}
